import generateSurvivorBuild, {
  evaluateSurvivorChaosZeroCombo
} from './generateSurvivorBuild'
import {
  BEST_BUILD_CONFLICTS,
  BOON_PERK_IDS,
  TOTEM_FINDER_PERK_IDS,
  survivorBestBuildPlans
} from '@/data/survivorBestBuildPlans'
import {
  getSurvivorPerkRoleIds,
  functionalRedundancyGroups,
  functionalRedundancyPolicy,
  survivorCentralWants,
  getSurvivorCentralWantWeight,
  getSurvivorRoleRecipe,
  getSurvivorRoleRecipeMatches
} from '@/data/survivorPerkRoles'
import { getSurvivorPerkStrengthScore } from '@/data/survivorPerkStrengths'
import {
  getSurvivorInteraction,
  isSurvivorBestBuildPairBlocked
} from '@/data/survivorPerkInteractions'
import {
  evaluateSurvivorPerkCompatibility,
  summarizeSurvivorBuildCompatibility
} from '@/data/survivorPerkCompatibility'
import {
  areSurvivorGameplayEquivalents,
  getSurvivorGameplayPerkId
} from '@/data/survivorPerkEquivalents'

const SATELLITE_SLOTS = [2, 3, 4]
const FALLBACK_OPTION_COUNT = 8
const GENERATED_SLOT_OPTION_COUNT = 8
const DIRECT_SYNERGY_MIN_PAIR_WEIGHT = 45
const LOADOUT_EXACT_MECHANIC_MIN_WEIGHT = 82
const LOADOUT_STRENGTH_WEIGHT = 0.7
const LOADOUT_CRAFT_VARIANT_ID = 'loadout_craft'
export const BEST_BUILD_MODES = {
  loadoutCraft: 'loadout_craft',
  synergyFocus: 'synergy_focus'
}
const GENERIC_GLUE_PERKS = [
  'windows_of_opportunity',
  'sprint_burst',
  'lithe',
  'resilience',
  'deja_vu',
  'kindred'
]

function buildPerkLookup (catalog) {
  return catalog.reduce((lookup, perk) => {
    lookup[perk.id] = perk
    return lookup
  }, {})
}

function normalizeLockedSlots (lockedSlots) {
  return Object.keys(lockedSlots || {}).reduce((locks, slotKey) => {
    const slot = Number(slotKey)
    const lock = lockedSlots[slotKey]
    if (!slot || SATELLITE_SLOTS.indexOf(slot) === -1) return locks
    if (typeof lock === 'string') {
      locks[slot] = { perkId: lock, enabled: true }
    } else if (lock && lock.perkId) {
      locks[slot] = {
        perkId: lock.perkId,
        enabled: lock.enabled !== false
      }
    }
    return locks
  }, {})
}

function uniqueOptions (options) {
  const seen = new Set()
  return (options || []).filter(option => {
    const gameplayId = option && getSurvivorGameplayPerkId(option.perkId)
    if (!option || !option.perkId || seen.has(gameplayId)) return false
    seen.add(gameplayId)
    return true
  })
}

function getOptionScore (centralPerkId, option) {
  if (!option) return 0
  const centralWant = getSurvivorCentralWantWeight(centralPerkId, option.perkId)
  return option.score || centralWant || 50
}

function getPairSynergyWeight (firstPerk, secondPerk) {
  if (!firstPerk || !secondPerk) return 0
  return evaluateSurvivorPerkCompatibility(firstPerk, secondPerk).score
}

function getLoadoutCraftScore (centralPerkId, candidatePerk, selectedPerks) {
  if (!candidatePerk) return 0
  const compatibility = summarizeSurvivorBuildCompatibility(candidatePerk, selectedPerks, centralPerkId)
  if (compatibility.blocked) return 0

  const strengthAdjustment = (getSurvivorPerkStrengthScore(candidatePerk) - 50) * LOADOUT_STRENGTH_WEIGHT

  return Math.max(0, Math.min(100, Math.round(
    compatibility.score + strengthAdjustment - getFunctionalRedundancyPenalty(candidatePerk.id, selectedPerks)
  )))
}

function getGenericGluePenalty (centralPerkId, option, perk, selectedPerks) {
  if (!perk || GENERIC_GLUE_PERKS.indexOf(option.perkId) === -1) return 0
  const manualScore = option.score || 0
  const centralWant = getSurvivorCentralWantWeight(centralPerkId, option.perkId)
  if (manualScore >= 82 || centralWant >= 82) return 0
  const hasRealContext = selectedPerks.some(selectedPerk => {
    return getPairSynergyWeight(selectedPerk, perk) >= 45
  })
  return hasRealContext ? 0 : 12
}

function getFunctionalRedundancyPenalty (perkId, selectedPerks) {
  if (!perkId || !selectedPerks.length) return 0

  return Object.keys(functionalRedundancyGroups).reduce((penalty, groupId) => {
    const group = functionalRedundancyGroups[groupId]
    if (!group || group.indexOf(perkId) === -1) return penalty

    const policy = functionalRedundancyPolicy[groupId] || { freeCount: 1, penaltyPerExtra: 18 }
    const selectedCount = selectedPerks.filter(selectedPerk => selectedPerk && group.indexOf(selectedPerk.id) !== -1).length
    if (selectedCount < policy.freeCount) return penalty

    return penalty + (selectedCount - policy.freeCount + 1) * policy.penaltyPerExtra
  }, 0)
}

function getRawOptionContextScore (centralPerkId, option, selectedPerks, catalogById) {
  const perk = catalogById[option.perkId]
  const centralPerk = catalogById[centralPerkId]
  const baseScore = getOptionScore(centralPerkId, option)
  if (!perk) return baseScore

  const centralPairWeight = getPairSynergyWeight(centralPerk, perk) * 0.18
  const funnelWeight = selectedPerks
    .filter(selectedPerk => selectedPerk && selectedPerk.id !== centralPerkId)
    .reduce((score, selectedPerk, index, previousPerks) => {
      const isLastPick = index === previousPerks.length - 1
      const multiplier = isLastPick ? 0.42 : 0.24
      return score + getPairSynergyWeight(selectedPerk, perk) * multiplier
    }, 0)
  const penalty = getGenericGluePenalty(centralPerkId, option, perk, selectedPerks)
  const redundancyPenalty = getFunctionalRedundancyPenalty(option.perkId, selectedPerks)

  return baseScore + centralPairWeight + funnelWeight - penalty - redundancyPenalty
}

function scoreOptionInContext (centralPerkId, option, selectedPerks, catalogById) {
  const rawScore = getRawOptionContextScore(centralPerkId, option, selectedPerks, catalogById)
  const baseScore = getOptionScore(centralPerkId, option)
  const contextDelta = rawScore - baseScore
  const displayDelta = contextDelta > 0 ? contextDelta * 0.25 : contextDelta
  return Math.max(0, Math.min(100, Math.round(baseScore + displayDelta)))
}

function sortOptions (centralPerkId, options) {
  return uniqueOptions(options).slice().sort((first, second) => {
    const scoreDifference = getOptionScore(centralPerkId, second) - getOptionScore(centralPerkId, first)
    if (scoreDifference !== 0) return scoreDifference
    return first.perkId.localeCompare(second.perkId)
  })
}

function sortOptionsForContext (centralPerkId, options, selectedPerks, catalogById) {
  return uniqueOptions(options).slice().sort((first, second) => {
    const scoreDifference = getRawOptionContextScore(centralPerkId, second, selectedPerks, catalogById) -
      getRawOptionContextScore(centralPerkId, first, selectedPerks, catalogById)
    if (scoreDifference !== 0) return scoreDifference
    return first.perkId.localeCompare(second.perkId)
  })
}

function isBoonPerk (perkId) {
  return BOON_PERK_IDS.indexOf(perkId) !== -1
}

function isTotemFinderPerk (perkId) {
  return TOTEM_FINDER_PERK_IDS.indexOf(perkId) !== -1
}

function getConflict (perkId, selectedPerkIds) {
  const equivalentPerkId = selectedPerkIds.find(selectedPerkId => {
    return selectedPerkId !== perkId && areSurvivorGameplayEquivalents(perkId, selectedPerkId)
  })
  if (equivalentPerkId) {
    return {
      type: 'gameplay-duplicate',
      perks: [perkId, equivalentPerkId],
      reason: 'These two perk names share the same gameplay effect and cannot occupy two build slots.'
    }
  }

  const explicitConflict = BEST_BUILD_CONFLICTS.find(conflict => {
    return conflict.perks.indexOf(perkId) !== -1 &&
      selectedPerkIds.some(selectedPerkId => selectedPerkId !== perkId && conflict.perks.indexOf(selectedPerkId) !== -1)
  })
  if (explicitConflict) return explicitConflict

  const blockedPerkId = selectedPerkIds.find(selectedPerkId => {
    return selectedPerkId !== perkId && isSurvivorBestBuildPairBlocked(perkId, selectedPerkId)
  })
  if (!blockedPerkId) return null

  const mechanic = getSurvivorInteraction(perkId, blockedPerkId)
  return {
    perks: [perkId, blockedPerkId],
    reason: mechanic && mechanic.reason
      ? mechanic.reason
      : 'These perks cannot form a reliable best-build package.'
  }
}

function isForbiddenInVariant (perkId, variant) {
  return Boolean(variant && variant.forbiddenPerks && variant.forbiddenPerks.indexOf(perkId) !== -1)
}

function makeOption (centralPerkId, option, source) {
  return Object.assign({}, option, {
    score: getOptionScore(centralPerkId, option),
    source: source || option.source || 'plan'
  })
}

function getCentralWantOptions (centralPerkId) {
  const wants = survivorCentralWants[centralPerkId] || {}
  return Object.keys(wants)
    .map(perkId => ({ perkId, score: wants[perkId], source: 'central-wants' }))
}

function getFallbackRollOptions (catalog, centralPerkId) {
  const roll = generateSurvivorBuild(catalog, {
    lockedPerks: [{ slot: 1, perkId: centralPerkId, influenceScoring: true }],
    blacklist: [],
    ownedOnly: false,
    ownedPerks: [],
    activeGameplayFilters: [],
    allowedTags: null,
    preservedSlots: [],
    chaos: 0
  })
  if (roll.status !== 'success') return []

  return roll.slots.slice(1).reduce((options, slot) => {
    options.push({
      perkId: slot.perkId,
      score: slot.scoring && slot.scoring.centralDirectWeight ? slot.scoring.centralDirectWeight : 60,
      source: 'chaos-zero'
    })
    if (slot.scoring && slot.scoring.qa && slot.scoring.qa.topCandidates) {
      slot.scoring.qa.topCandidates.forEach(candidate => {
        options.push({
          perkId: candidate.perkId,
          score: candidate.raw || 50,
          source: 'chaos-zero'
        })
      })
    }
    return options
  }, [])
}

function makeGeneratedOption (perkId, score, source) {
  return {
    perkId,
    score: Math.max(0, Math.min(100, Math.round(score))),
    source: source || 'generated'
  }
}

function getBaseGeneratedOptions (catalog, centralPerkId, catalogById) {
  const centralWantOptions = getCentralWantOptions(centralPerkId)
  const fallbackRollOptions = getFallbackRollOptions(catalog, centralPerkId)
  const baseOptions = centralWantOptions.length
    ? centralWantOptions.concat(fallbackRollOptions)
    : fallbackRollOptions

  if (baseOptions.length) {
    return sortOptions(centralPerkId, baseOptions)
      .filter(option => option.perkId !== centralPerkId)
      .filter(option => !getConflict(option.perkId, [centralPerkId]))
  }

  const centralPerk = catalogById[centralPerkId]
  return catalog
    .filter(perk => perk && perk.id !== centralPerkId)
    .filter(perk => !getConflict(perk.id, [centralPerkId]))
    .map(perk => {
      const pairWeight = getPairSynergyWeight(centralPerk, perk)
      const roleCount = getSurvivorPerkRoleIds(perk.id).length
      return makeGeneratedOption(perk.id, 42 + pairWeight * 0.35 + roleCount * 2, 'catalog-role')
    })
    .sort((first, second) => second.score - first.score)
}

function distributeOptionsAcrossSlots (centralPerkId, options, catalogById, strategy) {
  const recipe = getSurvivorRoleRecipe(centralPerkId)
  const sortedOptions = sortOptions(centralPerkId, options)
  return SATELLITE_SLOTS.map((slot, slotIndex) => ({
    id: `${strategy || 'direct'}_${slotIndex + 1}`,
    label: strategy === 'role' && recipe && recipe.slotNeeds[slotIndex]
      ? recipe.slotNeeds[slotIndex].name.replace(/_/g, ' ')
      : strategy === 'flexible'
        ? `Flexible choice ${slotIndex + 1}`
        : `Direct synergy ${slotIndex + 1}`,
    selectionOffset: strategy === 'role'
      ? (slotIndex === 0 ? 0 : 1)
      : strategy === 'flexible'
        ? Math.min(2, slotIndex + 1)
        : 0,
    options: sortedOptions.slice().sort((first, second) => {
      if (strategy === 'role' && recipe && recipe.slotNeeds[slotIndex]) {
        const slotName = recipe.slotNeeds[slotIndex].name
        const firstMatch = getSurvivorRoleRecipeMatches(centralPerkId, first.perkId).find(match => match.slotName === slotName)
        const secondMatch = getSurvivorRoleRecipeMatches(centralPerkId, second.perkId).find(match => match.slotName === slotName)
        const roleDifference = ((secondMatch && secondMatch.weight) || 0) - ((firstMatch && firstMatch.weight) || 0)
        if (roleDifference !== 0) return roleDifference
      }
      if (strategy === 'flexible') {
        const firstPerk = catalogById[first.perkId]
        const secondPerk = catalogById[second.perkId]
        const firstBlend = getOptionScore(centralPerkId, first) * 0.58 + getSurvivorPerkStrengthScore(firstPerk) * 0.42
        const secondBlend = getOptionScore(centralPerkId, second) * 0.58 + getSurvivorPerkStrengthScore(secondPerk) * 0.42
        if (secondBlend !== firstBlend) return secondBlend - firstBlend
      }
      return getOptionScore(centralPerkId, second) - getOptionScore(centralPerkId, first)
    }).slice(
      strategy === 'flexible' ? Math.min(slotIndex * 2, Math.max(0, sortedOptions.length - GENERATED_SLOT_OPTION_COUNT)) : 0,
      strategy === 'flexible' ? Math.min(slotIndex * 2, Math.max(0, sortedOptions.length - GENERATED_SLOT_OPTION_COUNT)) + GENERATED_SLOT_OPTION_COUNT : GENERATED_SLOT_OPTION_COUNT
    )
  }))
}

function collectForbiddenPerks (variants) {
  return Array.from(new Set((variants || []).reduce((forbiddenPerks, variant) => {
    return forbiddenPerks.concat(variant.forbiddenPerks || [])
  }, [])))
}

const LOADOUT_VARIANT_DEFINITIONS = [
  {
    id: LOADOUT_CRAFT_VARIANT_ID,
    title: 'Core recommendation',
    summary: 'Highest-scoring complete package after mechanic, role and redundancy checks.',
    advantages: ['Strongest modeled compatibility', 'Complete role coverage'],
    drawbacks: ['More specialised around the central perk']
  },
  {
    id: 'loadout_craft_alternative',
    title: 'Alternative package',
    summary: 'A genuinely different high-ranking package for another playstyle.',
    advantages: ['Changes at least two satellite perks when possible', 'Stays close to the top score'],
    drawbacks: ['Accepts a small modeled score trade-off']
  },
  {
    id: 'loadout_craft_flexible',
    title: 'Flexible compromise',
    summary: 'A third coherent route with broader room for personal preference.',
    advantages: ['Broader choice profile', 'Useful base for manual swaps'],
    drawbacks: ['Less concentrated on the central interaction']
  }
]

function createLoadoutCraftVariants (forbiddenPerks) {
  return LOADOUT_VARIANT_DEFINITIONS.map((definition, comboRank) => ({
    id: definition.id,
    mode: BEST_BUILD_MODES.loadoutCraft,
    title: definition.title,
    score: 80 - comboRank * 2,
    summary: definition.summary,
    advantages: definition.advantages,
    drawbacks: definition.drawbacks,
    generated: true,
    dynamic: true,
    comboRank,
    forbiddenPerks: forbiddenPerks || [],
    slots: SATELLITE_SLOTS.map((slot, index) => ({
      id: `cook_${comboRank}_${index + 1}`,
      label: `Cook slot ${index + 1}`,
      options: []
    }))
  }))
}

function getDirectSynergyOptions (catalog, centralPerkId, catalogById, fallbackOptions) {
  const centralPerk = catalogById[centralPerkId]
  const centralWantOptions = getCentralWantOptions(centralPerkId)
  const pairOptions = catalog
    .filter(perk => perk && perk.id !== centralPerkId)
    .filter(perk => !getConflict(perk.id, [centralPerkId]))
    .map(perk => {
      const compatibility = evaluateSurvivorPerkCompatibility(centralPerk, perk)
      if (compatibility.confidence !== 'exact' || compatibility.kind !== 'synergy') return null
      if (compatibility.score < LOADOUT_EXACT_MECHANIC_MIN_WEIGHT) return null
      const pairWeight = compatibility.score
      if (pairWeight < DIRECT_SYNERGY_MIN_PAIR_WEIGHT) return null
      return makeGeneratedOption(perk.id, 44 + pairWeight * 0.58, 'central-pair')
    })
    .filter(Boolean)

  const directOptions = sortOptions(centralPerkId, pairOptions.concat(centralWantOptions))
    .filter(option => option.perkId !== centralPerkId)
    .filter(option => !getConflict(option.perkId, [centralPerkId]))

  return directOptions.length ? directOptions : fallbackOptions
}

function getExactCentralMechanicOptions (catalog, centralPerkId, catalogById, variant) {
  const centralPerk = catalogById[centralPerkId]
  const options = catalog
    .filter(perk => perk && perk.id !== centralPerkId)
    .filter(perk => !isForbiddenInVariant(perk.id, variant))
    .filter(perk => !getConflict(perk.id, [centralPerkId]))
    .map(perk => {
      const compatibility = evaluateSurvivorPerkCompatibility(centralPerk, perk)
      if (compatibility.confidence !== 'exact' || compatibility.kind !== 'synergy') return null
      return {
        perkId: perk.id,
        score: compatibility.score,
        source: 'exact-mechanic',
        reason: compatibility.reason
      }
    })
    .filter(Boolean)
    .sort((first, second) => {
      const scoreDifference = second.score - first.score
      if (scoreDifference !== 0) return scoreDifference
      const spriteDifference = Number(Boolean(catalogById[second.perkId].hasSprite)) - Number(Boolean(catalogById[first.perkId].hasSprite))
      if (spriteDifference !== 0) return spriteDifference
      const strengthDifference = getSurvivorPerkStrengthScore(catalogById[second.perkId]) - getSurvivorPerkStrengthScore(catalogById[first.perkId])
      if (strengthDifference !== 0) return strengthDifference
      return first.perkId.localeCompare(second.perkId)
    })
  return uniqueOptions(options)
}

function createSynergyFocusVariants (centralPerkId, directOptions, baseOptions, forbiddenPerks, catalogById) {
  return [
    {
      id: 'direct_synergy',
      mode: BEST_BUILD_MODES.synergyFocus,
      title: 'Direct mechanics',
      score: 62,
      summary: 'Prioritises the strongest documented or curated links to the central perk.',
      advantages: ['Most central-perk value', 'Highest-confidence links first'],
      drawbacks: ['Can be narrow or situational'],
      generated: true,
      forbiddenPerks: forbiddenPerks || [],
      slots: distributeOptionsAcrossSlots(centralPerkId, directOptions, catalogById, 'direct')
    },
    {
      id: 'role_balance',
      mode: BEST_BUILD_MODES.synergyFocus,
      title: 'Role-balanced route',
      score: 59,
      summary: 'Fills three different jobs from the central perk recipe before chasing raw power.',
      advantages: ['Clear job per slot', 'Lower functional redundancy'],
      drawbacks: ['Some links are role-supported rather than mechanical'],
      generated: true,
      forbiddenPerks: forbiddenPerks || [],
      slots: distributeOptionsAcrossSlots(centralPerkId, directOptions.concat(baseOptions), catalogById, 'role')
    },
    {
      id: 'flexible_synergy',
      mode: BEST_BUILD_MODES.synergyFocus,
      title: 'Flexible power route',
      score: 56,
      summary: 'Blends central compatibility with individual perk strength and different swap pools.',
      advantages: ['More room for taste and comfort picks', 'Keeps independently useful perks'],
      drawbacks: ['Weakest direct link of the three routes'],
      generated: true,
      forbiddenPerks: forbiddenPerks || [],
      slots: distributeOptionsAcrossSlots(centralPerkId, directOptions.concat(baseOptions), catalogById, 'flexible')
    }
  ]
}

function createFallbackPlan (catalog, centralPerkId) {
  const catalogById = buildPerkLookup(catalog)
  const baseOptions = getBaseGeneratedOptions(catalog, centralPerkId, catalogById)
  const directOptions = getDirectSynergyOptions(catalog, centralPerkId, catalogById, baseOptions)
  const variants = createLoadoutCraftVariants()
    .concat(createSynergyFocusVariants(centralPerkId, directOptions, baseOptions, [], catalogById))

  const defaultLoadoutVariant = variants.find(variant => variant.mode === BEST_BUILD_MODES.loadoutCraft) || variants[0]

  return {
    centralPerkId,
    defaultVariant: defaultLoadoutVariant.id,
    defaultVariants: {
      [BEST_BUILD_MODES.loadoutCraft]: defaultLoadoutVariant.id,
      [BEST_BUILD_MODES.synergyFocus]: 'direct_synergy'
    },
    generated: true,
    variants
  }
}

export function getSurvivorBestBuildPlan (catalog, centralPerkId) {
  const manualPlan = survivorBestBuildPlans[centralPerkId]
  if (manualPlan) {
    const catalogById = buildPerkLookup(catalog)
    const baseOptions = getBaseGeneratedOptions(catalog, centralPerkId, catalogById)
    const directOptions = getDirectSynergyOptions(catalog, centralPerkId, catalogById, baseOptions)
    const forbiddenPerks = collectForbiddenPerks(manualPlan.variants)
    const loadoutVariants = createLoadoutCraftVariants(forbiddenPerks)
    const exactMechanicOptions = directOptions.filter(option => option.source === 'central-pair')
    const manualVariants = manualPlan.variants.map((variant, variantIndex) => Object.assign({}, variant, {
      mode: BEST_BUILD_MODES.synergyFocus,
      slots: variant.slots.map((slot, slotIndex) => Object.assign({}, slot, {
        options: uniqueOptions(exactMechanicOptions.concat(slot.options || [])),
        selectionOffset: variantIndex === 0 ? 0 : Math.min(2, variantIndex + (slotIndex % 2))
      }))
    }))
    const generatedVariants = createSynergyFocusVariants(centralPerkId, directOptions, baseOptions, forbiddenPerks, catalogById)
    const variants = loadoutVariants.concat(manualVariants, generatedVariants)

    return Object.assign({}, manualPlan, {
      centralPerkId,
      generated: false,
      defaultVariant: loadoutVariants[0].id,
      defaultVariants: {
        [BEST_BUILD_MODES.loadoutCraft]: loadoutVariants[0].id,
        [BEST_BUILD_MODES.synergyFocus]: manualPlan.defaultVariant
      },
      variants
    })
  }
  return createFallbackPlan(catalog, centralPerkId)
}

function scoreVariantForLocks (centralPerkId, variant, locks) {
  if (variant && variant.dynamic) return variant.score || 0
  return Object.keys(locks).reduce((score, slotKey) => {
    const lock = locks[slotKey]
    if (!lock || !lock.enabled || !lock.perkId) return score
    const slotIndex = SATELLITE_SLOTS.indexOf(Number(slotKey))
    const slot = variant.slots[slotIndex]
    const option = slot && (slot.options || []).find(candidate => candidate.perkId === lock.perkId)
    return score + (option ? getOptionScore(centralPerkId, option) : -35)
  }, variant.score || 0)
}

function variantSupportsLocks (variant, locks) {
  if (variant && variant.dynamic) return true
  return Object.keys(locks).every(slotKey => {
    const lock = locks[slotKey]
    if (!lock || !lock.enabled || !lock.perkId) return true
    const slotIndex = SATELLITE_SLOTS.indexOf(Number(slotKey))
    const slot = variant.slots[slotIndex]
    return Boolean(slot && (slot.options || []).some(option => option.perkId === lock.perkId))
  })
}

function getModeVariants (plan, mode) {
  const modeVariants = plan.variants.filter(variant => (variant.mode || BEST_BUILD_MODES.loadoutCraft) === mode)
  return modeVariants.length ? modeVariants : plan.variants
}

function selectVariant (plan, centralPerkId, locks, requestedVariantId, mode) {
  const candidateVariants = getModeVariants(plan, mode)
  const requestedVariant = requestedVariantId
    ? candidateVariants.find(variant => variant.id === requestedVariantId)
    : null
  if (requestedVariant && variantSupportsLocks(requestedVariant, locks)) return requestedVariant

  const modeDefaultVariantId = plan.defaultVariants && plan.defaultVariants[mode]
    ? plan.defaultVariants[mode]
    : plan.defaultVariant
  const defaultVariant = modeDefaultVariantId
    ? candidateVariants.find(variant => variant.id === modeDefaultVariantId)
    : null
  if (defaultVariant && Object.keys(locks).length === 0) return defaultVariant

  return candidateVariants.slice().sort((first, second) => {
    const scoreDifference = scoreVariantForLocks(centralPerkId, second, locks) - scoreVariantForLocks(centralPerkId, first, locks)
    if (scoreDifference !== 0) return scoreDifference
    return first.id.localeCompare(second.id)
  })[0]
}

function getSlotAlternativeOptions (plan, variant, slotIndex, centralPerkId, selectedPerks, catalogById) {
  const options = []
  const currentSlot = variant.slots[slotIndex]
  const variantMode = variant.mode || BEST_BUILD_MODES.loadoutCraft
  if (currentSlot) options.push(...currentSlot.options)
  if (slotIndex === 0) {
    plan.variants.forEach(planVariant => {
      if ((planVariant.mode || BEST_BUILD_MODES.loadoutCraft) !== variantMode) return
      const matchingSlot = planVariant.slots[slotIndex]
      if (matchingSlot) options.push(...matchingSlot.options)
    })
  }
  if (plan.generated || options.length === 0) {
    getCentralWantOptions(centralPerkId).slice(0, FALLBACK_OPTION_COUNT).forEach(option => options.push(option))
  }
  return sortOptionsForContext(centralPerkId, options, selectedPerks, catalogById)
}

function getLoadoutCraftOptions (catalog, centralPerkId, selectedPerks, selectedPerkIds, usedPerkIds, variant, catalogById) {
  const rankedOptions = catalog
    .filter(perk => perk && perk.id !== centralPerkId)
    .filter(perk => !usedPerkIds || !usedPerkIds.has(perk.id))
    .filter(perk => !isForbiddenInVariant(perk.id, variant))
    .filter(perk => !getConflict(perk.id, selectedPerkIds))
    .map(perk => ({
      perkId: perk.id,
      score: getLoadoutCraftScore(centralPerkId, perk, selectedPerks),
      source: 'compatibility'
    }))
    .sort((first, second) => {
      const scoreDifference = second.score - first.score
      if (scoreDifference !== 0) return scoreDifference
      const spriteDifference = Number(Boolean(catalogById[second.perkId] && catalogById[second.perkId].hasSprite)) - Number(Boolean(catalogById[first.perkId] && catalogById[first.perkId].hasSprite))
      if (spriteDifference !== 0) return spriteDifference
      return first.perkId.localeCompare(second.perkId)
    })
  return uniqueOptions(rankedOptions)
}

function chooseLoadoutCraftOptionForSlot (centralPerkId, catalog, variant, selectedPerks, selectedPerkIds, usedPerkIds, lock, catalogById, warnings) {
  if (lock && lock.enabled && lock.perkId) {
    const lockedPerk = catalogById[lock.perkId]
    const conflict = getConflict(lock.perkId, selectedPerkIds)
    if (conflict) warnings.push(conflict.reason)
    if (!conflict || conflict.type !== 'gameplay-duplicate') {
      return lockedPerk
        ? {
          perkId: lock.perkId,
          score: getLoadoutCraftScore(centralPerkId, lockedPerk, selectedPerks),
          source: 'locked'
        }
        : null
    }
  }

  return getLoadoutCraftOptions(catalog, centralPerkId, selectedPerks, selectedPerkIds, usedPerkIds, variant, catalogById)[0] || null
}

function chooseOptionForSlot (centralPerkId, slotPlan, variant, selectedPerks, selectedPerkIds, usedPerkIds, lock, catalogById, warnings) {
  if (lock && lock.enabled && lock.perkId) {
    const lockedOption = (slotPlan.options || []).find(option => option.perkId === lock.perkId) || {
      perkId: lock.perkId,
      score: getSurvivorCentralWantWeight(centralPerkId, lock.perkId) || 50,
      source: 'locked'
    }
    const conflict = getConflict(lock.perkId, selectedPerkIds)
    if (conflict) warnings.push(conflict.reason)
    if (!conflict || conflict.type !== 'gameplay-duplicate') {
      return Object.assign(makeOption(centralPerkId, lockedOption, 'locked'), {
        score: scoreOptionInContext(centralPerkId, lockedOption, selectedPerks, catalogById)
      })
    }
  }

  const options = sortOptionsForContext(centralPerkId, slotPlan.options, selectedPerks, catalogById)
  const selectionOffset = Math.max(0, Number(slotPlan.selectionOffset) || 0)
  let viableOptionIndex = 0
  let firstViableOption = null
  for (let index = 0; index < options.length; index++) {
    const option = options[index]
    if (!catalogById[option.perkId]) continue
    if (usedPerkIds.has(option.perkId)) continue
    if (isForbiddenInVariant(option.perkId, variant)) continue
    if (getConflict(option.perkId, selectedPerkIds)) continue
    const scoredOption = Object.assign(makeOption(centralPerkId, option), {
      score: scoreOptionInContext(centralPerkId, option, selectedPerks, catalogById)
    })
    if (!firstViableOption) firstViableOption = scoredOption
    if (viableOptionIndex >= selectionOffset) return scoredOption
    viableOptionIndex += 1
  }

  return firstViableOption
}

function findFallbackOption (centralPerkId, catalog, selectedPerks, selectedPerkIds, usedPerkIds, variant, catalogById) {
  const fallbackOptions = sortOptions(centralPerkId, getCentralWantOptions(centralPerkId).concat(getFallbackRollOptions(catalog, centralPerkId)))
  const option = sortOptionsForContext(centralPerkId, fallbackOptions, selectedPerks, catalogById).find(option => {
    if (usedPerkIds.has(option.perkId)) return false
    if (isForbiddenInVariant(option.perkId, variant)) return false
    return !getConflict(option.perkId, selectedPerkIds)
  }) || null
  return option
    ? Object.assign(makeOption(centralPerkId, option), {
      score: scoreOptionInContext(centralPerkId, option, selectedPerks, catalogById)
    })
    : null
}

function getFinderOption (centralPerkId, catalogById, usedPerkIds, selectedPerkIds, variant) {
  const options = TOTEM_FINDER_PERK_IDS
    .filter(perkId => catalogById[perkId])
    .filter(perkId => !usedPerkIds.has(perkId))
    .map((perkId, index) => ({
      perkId,
      score: getSurvivorCentralWantWeight(centralPerkId, perkId) || (90 - index * 8),
      source: 'boon-rule'
    }))
  return sortOptions(centralPerkId, options).find(option => {
    if (isForbiddenInVariant(option.perkId, variant)) return false
    return !getConflict(option.perkId, selectedPerkIds)
  }) || null
}

function applyBoonFinderRule (build, centralPerkId, catalogById, lockedSlots, variant) {
  const perkIds = build.slots.map(slot => slot.perkId)
  const boonCount = perkIds.filter(isBoonPerk).length
  if (boonCount < 2 || perkIds.some(isTotemFinderPerk)) return build

  const usedPerkIds = new Set(perkIds)
  const finderOption = getFinderOption(centralPerkId, catalogById, usedPerkIds, perkIds, variant)
  if (!finderOption) {
    build.warnings.push('This build has multiple boons but no available totem finder.')
    return build
  }

  const replaceableSlots = build.slots
    .filter(slot => slot.slot !== 1 && !(lockedSlots[slot.slot] && lockedSlots[slot.slot].enabled))
    .sort((first, second) => {
      if (first.perkId === 'counterforce') return -1
      if (second.perkId === 'counterforce') return 1
      if (isBoonPerk(first.perkId) !== isBoonPerk(second.perkId)) return isBoonPerk(first.perkId) ? 1 : -1
      return first.score - second.score
    })

  if (!replaceableSlots.length) {
    build.warnings.push('Locked build has multiple boons but no totem finder. Unlock a slot to satisfy the boon rule.')
    return build
  }

  const replacedSlot = replaceableSlots[0]
  replacedSlot.perk = catalogById[finderOption.perkId]
  replacedSlot.perkId = finderOption.perkId
  replacedSlot.label = 'Totem finder'
  replacedSlot.score = finderOption.score
  replacedSlot.note = 'Forced because the build contains at least two boon perks.'
  replacedSlot.source = 'boon-rule'
  build.warnings.push('Boon rule applied: two or more boon perks require a totem finder. Counterforce is intentionally excluded from that finder list.')
  return build
}

function sanitizeBuildAlternatives (build) {
  build.slots.forEach(slot => {
    if (slot.slot === 1) return
    const selectedOtherPerkIds = build.slots
      .filter(selectedSlot => selectedSlot.slot !== slot.slot)
      .map(selectedSlot => selectedSlot.perkId)
    const selectedOtherSet = new Set(selectedOtherPerkIds)
    slot.alternatives = uniqueOptions(slot.alternatives || [])
      .filter(option => option.perkId !== slot.perkId)
      .filter(option => !selectedOtherSet.has(option.perkId))
      .filter(option => !getConflict(option.perkId, selectedOtherPerkIds))
      .filter(option => !isForbiddenInVariant(option.perkId, build.variant))
    if (build.mode !== BEST_BUILD_MODES.loadoutCraft) {
      slot.alternatives = slot.alternatives.slice(0, 5)
    }
  })
  return build
}

function annotateBuildCompatibility (build, catalogById) {
  build.slots.forEach(slot => {
    if (slot.slot === 1) {
      const usage = slot.perk && slot.perk.interactionKnowledge && slot.perk.interactionKnowledge.usage
      slot.compatibility = {
        score: 100,
        kind: 'central',
        confidence: usage ? 'exact' : 'curated',
        confidenceLabel: usage ? 'Exact mechanic' : 'Build anchor',
        reason: usage || 'Selected as the build identity anchor.'
      }
      slot.note = slot.compatibility.reason
      return
    }

    const otherPerks = build.slots
      .filter(otherSlot => otherSlot.slot !== slot.slot)
      .map(otherSlot => otherSlot.perk)
    slot.compatibility = summarizeSurvivorBuildCompatibility(slot.perk, otherPerks, build.centralPerkId)
    slot.note = slot.compatibility.reason
    slot.alternatives = (slot.alternatives || []).map(option => {
      const candidate = catalogById[option.perkId]
      if (!candidate) return option
      return Object.assign({}, option, {
        compatibility: summarizeSurvivorBuildCompatibility(candidate, otherPerks, build.centralPerkId)
      })
    })
  })
  return build
}

function rankLoadoutCraftAlternativesByCombo (build, catalog, catalogById) {
  if (build.mode !== BEST_BUILD_MODES.loadoutCraft) return build

  build.slots.forEach(slot => {
    if (slot.slot === 1) return
    const otherPerkIds = build.slots
      .filter(selectedSlot => selectedSlot.slot !== slot.slot)
      .map(selectedSlot => selectedSlot.perkId)
    const otherPerkIdSet = new Set(otherPerkIds)
    const scoredOptions = catalog
      .filter(perk => perk && perk.id !== build.centralPerkId)
      .filter(perk => !otherPerkIdSet.has(perk.id))
      .filter(perk => !isForbiddenInVariant(perk.id, build.variant))
      .filter(perk => !getConflict(perk.id, otherPerkIds))
      .map(perk => {
        const satellitePerkIds = build.slots
          .filter(selectedSlot => selectedSlot.slot !== 1)
          .map(selectedSlot => selectedSlot.slot === slot.slot ? perk.id : selectedSlot.perkId)
        const combo = evaluateSurvivorChaosZeroCombo(catalog, build.centralPerkId, satellitePerkIds)
        return combo
          ? {
            perkId: perk.id,
            rawScore: combo.globalComboScore,
            strengthScore: getSurvivorPerkStrengthScore(perk),
            source: 'global-combo'
          }
          : null
      })
      .filter(Boolean)
      .sort((first, second) => {
        const rawDifference = second.rawScore - first.rawScore
        if (rawDifference !== 0) return rawDifference
        const strengthDifference = second.strengthScore - first.strengthScore
        if (strengthDifference !== 0) return strengthDifference
        return first.perkId.localeCompare(second.perkId)
      })

    if (!scoredOptions.length) return
    const topRawScore = scoredOptions[0].rawScore
    const normalizedOptions = scoredOptions.map(option => ({
      perkId: option.perkId,
      score: Math.max(0, Math.min(100, Math.round(100 - (topRawScore - option.rawScore)))),
      source: option.source
    }))
    const selectedOption = normalizedOptions.find(option => option.perkId === slot.perkId)
    if (selectedOption) slot.score = selectedOption.score
    slot.alternatives = normalizedOptions.filter(option => option.perkId !== slot.perkId && catalogById[option.perkId])
  })
  return build
}

function getOptimizedLoadout (catalog, centralPerkId, lockedSlots, variant) {
  const lockedPerkIds = Object.keys(lockedSlots).reduce((ids, slotKey) => {
    const lock = lockedSlots[slotKey]
    if (lock && lock.enabled && lock.perkId) ids.push(lock.perkId)
    return ids
  }, [])
  const protectedPerkIds = new Set([centralPerkId].concat(lockedPerkIds))
  const eligibleCatalog = catalog.filter(perk => {
    if (!perk) return false
    if (protectedPerkIds.has(perk.id)) return true
    if (isForbiddenInVariant(perk.id, variant)) return false
    return !getConflict(perk.id, Array.from(protectedPerkIds))
  })
  const lockedPerks = [{
    slot: 1,
    perkId: centralPerkId,
    influenceScoring: true
  }].concat(Object.keys(lockedSlots).reduce((locks, slotKey) => {
    const lock = lockedSlots[slotKey]
    if (!lock || !lock.enabled || !lock.perkId) return locks
    locks.push({
      slot: Number(slotKey),
      perkId: lock.perkId,
      influenceScoring: true
    })
    return locks
  }, []))
  const result = generateSurvivorBuild(eligibleCatalog, {
    lockedPerks,
    blacklist: [],
    ownedOnly: false,
    ownedPerks: [],
    activeGameplayFilters: [],
    allowedTags: null,
    preservedSlots: [],
    chaos: 0,
    chaosZeroComboRank: variant.comboRank || 0
  })

  return result.status === 'success' ? result : null
}
export default function generateSurvivorBestBuild (catalog, config) {
  const catalogById = buildPerkLookup(catalog)
  const centralPerkId = config && config.centralPerkId
  const centralPerk = catalogById[centralPerkId]
  if (!centralPerk) {
    return {
      status: 'invalid-config',
      errors: [{ code: 'INVALID_CENTRAL_PERK', perkId: centralPerkId }],
      slots: []
    }
  }

  const lockedSlots = normalizeLockedSlots(config.lockedSlots)
  const plan = getSurvivorBestBuildPlan(catalog, centralPerkId)
  const mode = config.mode || BEST_BUILD_MODES.loadoutCraft
  const variant = selectVariant(plan, centralPerkId, lockedSlots, config.variantId, mode)
  const isLoadoutCraft = mode === BEST_BUILD_MODES.loadoutCraft
  const optimizedLoadout = isLoadoutCraft
    ? getOptimizedLoadout(catalog, centralPerkId, lockedSlots, variant)
    : null
  const optimizedSlotsByNumber = optimizedLoadout
    ? optimizedLoadout.slots.reduce((lookup, slot) => {
      lookup[slot.slot] = slot
      return lookup
    }, {})
    : {}
  const activeLockedPerkIds = new Set(Object.keys(lockedSlots).reduce((perkIds, slotKey) => {
    const lock = lockedSlots[slotKey]
    if (lock && lock.enabled && lock.perkId) perkIds.push(lock.perkId)
    return perkIds
  }, []))
  const exactCentralMechanicOptions = isLoadoutCraft
    ? getExactCentralMechanicOptions(catalog, centralPerkId, catalogById, variant)
    : []
  let exactCentralMechanicCovered = exactCentralMechanicOptions.some(option => activeLockedPerkIds.has(option.perkId))
  const usedPerkIds = new Set([centralPerkId])
  const selectedPerkIds = [centralPerkId]
  const selectedPerks = [centralPerk]
  const warnings = []

  const slots = [{
    slot: 1,
    perk: centralPerk,
    perkId: centralPerk.id,
    label: 'Central perk',
    score: 100,
    locked: true,
    source: 'central',
    alternatives: []
  }]

  SATELLITE_SLOTS.forEach((slotNumber, slotIndex) => {
    const slotPlan = variant.slots[slotIndex] || {
      id: `fallback_${slotIndex + 1}`,
      label: `Synergy ${slotIndex + 1}`,
      options: getCentralWantOptions(centralPerkId)
    }
    const lock = lockedSlots[slotNumber]
    const alternatives = isLoadoutCraft
      ? getLoadoutCraftOptions(catalog, centralPerkId, selectedPerks, selectedPerkIds, usedPerkIds, variant, catalogById)
      : getSlotAlternativeOptions(plan, variant, slotIndex, centralPerkId, selectedPerks, catalogById)
    const optimizedSlot = optimizedSlotsByNumber[slotNumber]
    let selectedOption = null
    if (isLoadoutCraft &&
      !(lock && lock.enabled) &&
      !exactCentralMechanicCovered) {
      const exactMechanicOption = exactCentralMechanicOptions.find(option => {
        if (usedPerkIds.has(option.perkId) || activeLockedPerkIds.has(option.perkId)) return false
        return !getConflict(option.perkId, selectedPerkIds.concat(Array.from(activeLockedPerkIds)))
      })
      if (exactMechanicOption) {
        selectedOption = {
          perkId: exactMechanicOption.perkId,
          score: getLoadoutCraftScore(centralPerkId, catalogById[exactMechanicOption.perkId], selectedPerks),
          source: exactMechanicOption.source,
          label: 'Verified mechanic',
          note: exactMechanicOption.reason
        }
      }
    }
    if (isLoadoutCraft &&
      !selectedOption &&
      optimizedSlot &&
      !usedPerkIds.has(optimizedSlot.perkId) &&
      !getConflict(optimizedSlot.perkId, selectedPerkIds)) {
      const optimizedPerk = catalogById[optimizedSlot.perkId]
      const optimizedLink = optimizedSlot.scoring &&
        optimizedSlot.scoring.chaosZeroCentralLink
      const roleSlotName = optimizedLink && optimizedLink.roleSlotName
      const compatibilityScore = getLoadoutCraftScore(centralPerkId, optimizedPerk, selectedPerks)
      selectedOption = {
        perkId: optimizedSlot.perkId,
        score: Math.max(
          compatibilityScore,
          Math.min(100, Math.round((optimizedLink && optimizedLink.centralDirectWeight) || 0))
        ),
        source: lock && lock.enabled ? 'locked' : 'global-combo',
        label: roleSlotName
          ? roleSlotName.replace(/_/g, ' ').replace(/^./, character => character.toUpperCase())
          : slotPlan.label,
        note: lock && lock.enabled
          ? ''
          : 'Selected from the highest-scoring complete build after role coverage and redundancy checks.'
      }
    }
    if (!selectedOption) {
      selectedOption = isLoadoutCraft
        ? chooseLoadoutCraftOptionForSlot(centralPerkId, catalog, variant, selectedPerks, selectedPerkIds, usedPerkIds, lock, catalogById, warnings)
        : chooseOptionForSlot(centralPerkId, slotPlan, variant, selectedPerks, selectedPerkIds, usedPerkIds, lock, catalogById, warnings)
    }
    if (!selectedOption && !isLoadoutCraft) {
      selectedOption = findFallbackOption(centralPerkId, catalog, selectedPerks, selectedPerkIds, usedPerkIds, variant, catalogById)
    }
    if (!selectedOption || !catalogById[selectedOption.perkId]) return

    if (exactCentralMechanicOptions.some(option => option.perkId === selectedOption.perkId)) {
      exactCentralMechanicCovered = true
    }

    usedPerkIds.add(selectedOption.perkId)
    selectedPerkIds.push(selectedOption.perkId)
    selectedPerks.push(catalogById[selectedOption.perkId])
    slots.push({
      slot: slotNumber,
      perk: catalogById[selectedOption.perkId],
      perkId: selectedOption.perkId,
      label: selectedOption.label || slotPlan.label,
      score: selectedOption.score,
      note: selectedOption.note || '',
      locked: selectedOption.source === 'locked',
      source: selectedOption.source,
      alternatives: (isLoadoutCraft ? alternatives : alternatives.slice(0, 10))
        .filter(option => option.perkId !== centralPerkId)
        .filter(option => catalogById[option.perkId])
        .map(option => Object.assign(makeOption(centralPerkId, option), {
          score: isLoadoutCraft
            ? option.score
            : scoreOptionInContext(centralPerkId, option, selectedPerks.slice(0, -1), catalogById)
        }))
    })
  })

  const build = {
    status: 'success',
    centralPerk,
    centralPerkId,
    plan,
    variant,
    mode,
    planSource: plan.generated ? 'generated' : 'manual',
    slots,
    warnings: Array.from(new Set(warnings))
  }

  const finalizedBuild = applyBoonFinderRule(build, centralPerkId, catalogById, lockedSlots, variant)
  rankLoadoutCraftAlternativesByCombo(finalizedBuild, catalog, catalogById)
  sanitizeBuildAlternatives(finalizedBuild)
  return annotateBuildCompatibility(finalizedBuild, catalogById)
}
